// Wires the "Replace video" widget the academy edit modals mount
// for simple-mode courses + video-kind lessons. POST metadata +
// file size to the configured endpoint, run tus against the
// returned CF URL, reload the page on success so the new video
// appears in the player.

import {
  tusUpload,
  formatBytes,
  throttleByIntegerPercent,
} from "./presentation_tus.js";
import { onceGuard } from "./bind_once.js";
import { postForm } from "./presigned_upload.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-video-replace]";

export function initAcademyVideoReplace(root = document) {
  if (!root.querySelectorAll) return;
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireWidget);
}

function wireWidget(widget) {
  if (!firstBind(widget)) return;

  const endpoint = widget.dataset.endpoint;
  if (!endpoint) return;

  const fileInput = widget.querySelector("[data-video-replace-input]");
  const dropzone = widget.querySelector("[data-video-replace-dropzone]");
  const empty = widget.querySelector("[data-video-replace-empty]");
  const selected = widget.querySelector("[data-video-replace-selected]");
  const nameEl = widget.querySelector("[data-video-replace-name]");
  const sizeEl = widget.querySelector("[data-video-replace-size]");
  const errorEl = widget.querySelector("[data-video-replace-error]");
  const submit = widget.querySelector("[data-video-replace-submit]");
  const uploadingEl = widget.querySelector("[data-video-replace-uploading]");
  const progressBar = widget.querySelector("[data-video-replace-progress-bar]");
  const progressMessage = widget.querySelector(
    "[data-video-replace-progress-message]",
  );
  const progressPercent = widget.querySelector(
    "[data-video-replace-progress-percent]",
  );

  if (!fileInput || !submit) return;

  if (dropzone) {
    dropzone.addEventListener("click", () => fileInput.click());
    dropzone.addEventListener("dragenter", () => {
      dropzone.dataset.dragging = "true";
    });
    dropzone.addEventListener("dragover", (event) => event.preventDefault());
    dropzone.addEventListener("dragleave", (event) => {
      if (!dropzone.contains(event.relatedTarget)) {
        dropzone.dataset.dragging = "false";
      }
    });
    dropzone.addEventListener("drop", (event) => {
      event.preventDefault();
      dropzone.dataset.dragging = "false";
      const dt = event.dataTransfer;
      if (!dt || !dt.files || dt.files.length === 0) return;
      fileInput.files = dt.files;
      fileInput.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  fileInput.addEventListener("change", () => {
    const file = fileInput.files && fileInput.files[0];
    if (!file) {
      if (empty) empty.style.opacity = "1";
      if (selected) selected.style.opacity = "0";
      return;
    }
    if (empty) empty.style.opacity = "0";
    if (selected) selected.style.opacity = "1";
    if (nameEl) nameEl.textContent = file.name;
    if (sizeEl) sizeEl.textContent = formatBytes(file.size);
  });

  submit.addEventListener("click", async () => {
    const file = fileInput.files && fileInput.files[0];
    if (!file) {
      showError(errorEl, "Choose a video before submitting.");
      return;
    }

    setSubmitting(submit, true);
    hideError(errorEl);

    const params = new URLSearchParams();
    params.append("file_size_bytes", String(file.size));

    let payload;
    try {
      payload = await postForm(endpoint, params);
    } catch (err) {
      console.error("academy.video.replace: metadata POST failed", err);
      showError(
        errorEl,
        err.userMessage || "Could not replace the video. Please try again.",
      );
      setSubmitting(submit, false);
      return;
    }

    if (!payload || !payload.upload_url) {
      showError(errorEl, "Server response was missing upload details.");
      setSubmitting(submit, false);
      return;
    }

    if (uploadingEl) uploadingEl.classList.remove("hidden");
    setProgress(progressBar, progressPercent, progressMessage, 0, file.size, 0);

    tusUpload({
      file,
      uploadURL: payload.upload_url,
      onError: (err) => {
        // The response detail is the only clue to why an upload died,
        // and it never reaches the page: the copy below stays generic
        // on purpose, so log enough to diagnose the next report.
        console.error("academy.video.replace: tus upload failed", {
          message: err?.message,
          status: err?.originalResponse?.getStatus?.(),
          body: err?.originalResponse?.getBody?.(),
        });
        showError(
          errorEl,
          "Upload failed. Your current video is untouched — try again.",
        );
        setSubmitting(submit, false);
      },
      onProgress: throttleByIntegerPercent((uploaded, total, pct) => {
        setProgress(
          progressBar,
          progressPercent,
          progressMessage,
          uploaded,
          total,
          pct,
        );
      }),
      onSuccess: () => {
        setProgress(
          progressBar,
          progressPercent,
          progressMessage,
          file.size,
          file.size,
          100,
        );
        if (progressBar) {
          progressBar.classList.remove("bg-brand-500");
          progressBar.classList.add("bg-emerald-500");
        }
        if (progressMessage) {
          progressMessage.textContent =
            "Upload complete. Reloading to show your new video…";
        }
        // Hard reload so the player picks up the new cf_video_uid.
        setTimeout(() => window.location.reload(), 600);
      },
    });
  });
}

function setProgress(bar, percentEl, messageEl, uploaded, total, pct) {
  if (bar) bar.style.width = `${pct}%`;
  if (percentEl) percentEl.textContent = `${pct}%`;
  if (messageEl) {
    messageEl.textContent = `Uploading ${formatBytes(uploaded)} of ${formatBytes(total)}`;
  }
}

function showError(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}

function hideError(el) {
  if (!el) return;
  el.classList.add("hidden");
  el.textContent = "";
}

function setSubmitting(btn, isSubmitting) {
  if (!btn) return;
  btn.disabled = isSubmitting;
  btn.classList.toggle("opacity-60", isSubmitting);
  btn.classList.toggle("cursor-not-allowed", isSubmitting);
}
